import React, { useContext, useState, useEffect, useRef } from "react";
import { UserStateContext } from "../../../components/context/user-state-context.js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { PageHeaderMui } from "../../../components/index.js";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CallMadeIcon,
  CheckCircleIcon,
  CloseIcon,
  ElevationIcon,
  EventNoteIcon,
  MailboxIcon,
  MonetizationOnIcon,
  OpenFullIcon,
  OpenInNewWindowIcon,
  SendIcon,
  ShieldPersonIcon,
  SMSOutgoingIcon,
  StatusDotIcon,
} from "../../../components/mui/index.js";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { DatePicker } from "@mui/x-date-pickers";
import EnhancedTable from "../../../components/mui/enhanced-table/enhanced-table.component.js";
import StackedSnackbar from "../../../components/mui/snackbar/StackedSnackbar.js";
import { SingleSelectFilter } from "../../../components/mui/filter/single-select.component.js";
import {
  createQBMailerInvoiceWithOverage,
  getQBMailerInvoices,
} from "../../../shared/billing.api.js";
import { Loader } from "../../../components/loader/loader.component.js";

import { ApiStatusBox } from "../tools/api-status-box.component.js";
import { CacheStatusBox } from "../tools/cache-status-box.component.js";
import {
  renderInvoiceStatuses,
  generateLogsTooltip,
} from "./helpers/tooltips.component.js";
import { OpenInActions } from "./helpers/open-in-actions.component.js";
import { OverageSinglePopper } from "../quickbooks-invoice-list/helpers/overage-single-popper.js";
import { useSingleInvoiceActions } from "./actions/single-action.component.js";
import { QBMailerDetailModal } from "./detail-modal.component.js";
import CONSTANTS from "../../../shared/constants.js";

dayjs.extend(relativeTime);

export const QBMailerInvoiceList = () => {
  const { user } = useContext(UserStateContext);
  // Default to current month
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [renderIteration, setRenderIteration] = useState(0);
  const [errors, setErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [apiStatusLoading, setApiStatusLoading] = useState(false);
  const [apiStatusError, setApiStatusError] = useState(null);
  const snackbarRef = useRef(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const single = useSingleInvoiceActions({
    renderIteration,
    setRenderIteration,
    snackbarRef,
    setIsLoading,
  });

  const filterOptions = [
    {
      component: SingleSelectFilter,
      label: "Invoice Status",
      parentToolTip:
        "Filter invoices based on their current status in the billing workflow",
      key: "invoice_status",
      defaultValue: "true",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          labelTooltip: "Show all quickbooks invoices for the selected month",
          isDefault: true,
        },
      ],
    },
  ];

  const [filters, setFilters] = React.useState({});

  const handleRecentlyUpdatedInvoices = (invoices) => {
    //TODO once i decide on how to do cache refreshing for qb mailer invoices, then implement it here
    /*    refreshCacheForQBInvoices({
      invoices: invoices,
      start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
    }).then((e) => {
      setRenderIteration(renderIteration + 1);
    }); */
    setRenderIteration(renderIteration + 1);
  };

  const handleRowClick = (row) => {
    // Pass the entire row to the modal, not just invoiceId
    setSelectedRowData(row?.account_id);
    setDetailModalOpen(true);
  };

  const headCells = [
    {
      key: "account_name",
      label: "Account Name",
      width: "6%",
      displayComponent: ({ data }) => {
        const hasCinchAccount = !!data?.account_account_name;
        const hasSnowflakeAccount = !!data?.snowflake_account_name;
        if (hasCinchAccount && hasSnowflakeAccount) {
          return (
            <Tooltip
              title={
                <Typography>
                  Snowflake Account name: {data?.snowflake_account_name}
                </Typography>
              }
              arrow
            >
              <Typography sx={{}}>{data.account_account_name}</Typography>
            </Tooltip>
          );
        } else if (hasCinchAccount) {
          return (
            <Tooltip
              title={
                <Typography>
                  Cinch Account Name: {data?.account_account_name}
                </Typography>
              }
              arrow
            >
              <Typography sx={{}}>{data.account_account_name}</Typography>
            </Tooltip>
          );
        } else if (hasSnowflakeAccount) {
          return (
            <Tooltip
              title={
                <Typography>
                  Snowflake Account name: {data?.snowflake_account_name}
                </Typography>
              }
              arrow
            >
              <Typography sx={{}}>{data.snowflake_account_name}</Typography>
            </Tooltip>
          );
        } else {
          return <Typography sx={{}}>-- No Account Linked --</Typography>;
        }
      },
    },
    {
      key: "qb_customer_display_name",
      label: "Company Name",
      width: "6%",
      displayComponent: ({ data }) => (
        <Tooltip title={data?.qb_customer_fully_qualified_name || ""} arrow>
          <Typography sx={{}}>
            {data?.qb_customer_display_name || "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "overage_usage",
      label: "Overage Usage",
      width: "6%",
      displayComponent: ({ data }) => (
        <Tooltip
          title={
            <Typography>overage_per_unit {data?.overage_per_unit}</Typography>
          }
          arrow
        >
          <Typography sx={{}}>{data?.overage_usage}</Typography>
        </Tooltip>
      ),
    },

    {
      noSort: true,
      key: "logs",
      label: "Logs",
      width: "10%",
      displayComponent: ({ data }) => (
        <>
          {data.invoice_logs && data.invoice_logs.length > 0 ? (
            <Tooltip
              title={generateLogsTooltip(data?.invoice_logs, data?.invoices)}
            >
              <Typography>
                {data?.invoice_logs?.length} log
                {data?.invoice_logs?.length > 1 ? "s" : ""}
              </Typography>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                <Typography>
                  This either means we haven't attempted to make any changes to
                  this invoice, or if some error is obvious, then not we're
                  logging that error
                </Typography>
              }
            >
              <Typography sx={{}}> No logs found </Typography>
            </Tooltip>
          )}
        </>
      ),
    },

    {
      key: "EmailStatus",
      label: "Invoice Status",
      noSort: true,
      width: "10%",
      displayComponent: ({ data }) => renderInvoiceStatuses(data),
    },

    {
      key: "actions",
      label: "Actions",
      width: "5%",
      noSort: true,
      displayComponent: ({ data }) => (
        <>
          <OpenInActions data={data} />

          <Tooltip
            title={
              <Typography>
                {!data.account_id
                  ? "Please Link account first"
                  : "Open Mailer Invoice Creation & Overage Update Modal"}
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!data.account_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account before charging customer`,
                  });
                  return;
                }

                single.openCreateInvoiceAndOveragesModal(
                  { ...data, selectedMonth },
                  (result) => {
                    if (result) {
                      handleRecentlyUpdatedInvoices();
                    }
                  }
                );
              }}
            >
              <EventNoteIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              <Typography>
                {!data.account_id
                  ? "Please Link account first"
                  : data?.invoices?.length == 0
                  ? "Create QB Mailer Invoice first"
                  : !data?.contract_third_party_info?.stripe_customer_id
                  ? "No stripe customer id found"
                  : "Open Charge Customer in Stripe Modal"}
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!data.account_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account before charging customer`,
                  });
                  return;
                }
                if (!data?.contract_third_party_info?.stripe_customer_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `No Stripe Customer ID found. Please ensure the linked account has a valid Stripe Customer ID before charging.`,
                  });
                  return;
                }
                if (!data?.invoices?.length || data?.invoices?.length == 0) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please create a QB Mailer Invoice before charging the customer.`,
                  });
                  return;
                }

                single.openChargeCustomerModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices();
                  }
                });
              }}
            >
              <MonetizationOnIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              <Typography>
                {!data.account_id
                  ? "Please link an account first"
                  : data?.invoices?.length == 0
                  ? "Create QB Mailer Invoice first"
                  : "Open Mark as Paid Modal"}
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!data.account_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account before charging customer`,
                  });
                  return;
                }

                if (!data?.invoices?.length || data?.invoices?.length == 0) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please create a QB Mailer Invoice before charging the customer.`,
                  });
                  return;
                }

                single.openMarkPaidModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices();
                  }
                });
              }}
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              <Typography>
                {!data.account_id
                  ? "Please link an account first"
                  : data?.invoices?.length == 0
                  ? "Create QB Mailer Invoice first"
                  : "Open Send Email Invoice Modal"}
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!data.account_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account before charging customer`,
                  });
                  return;
                }

                if (!data?.invoices?.length || data?.invoices?.length == 0) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please create a QB Mailer Invoice before charging the customer.`,
                  });
                  return;
                }

                single.openSendInvoiceModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices();
                  }
                });
              }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const QBInvoicesApiWrapper = () => {
    return (params) =>
      getQBMailerInvoices({
        start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
        ...params,
      });
  };

  const actionButtons = {
    displayComponent: ({ data }) => <Stack direction="row" spacing={2}></Stack>,
  };

  return (
    <>
      <div className="wrapper">
        <PageHeaderMui
          type="QuickBooks Mailer Invoice List"
          icon={<ShieldPersonIcon />}
        />
        <Stack spacing={2}>
          <Box sx={{ px: "20px", pt: "20px" }}></Box>
          <div className="mui-wrapper">
            <Stack spacing={2} sx={{ p: "20px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "space-between",
                  width: "100%",
                }}
              >
                <Stack spacing={3} sx={{ width: "fit-content" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Month (Invoice Date)"
                      views={["month", "year"]}
                      disableFuture
                      value={selectedMonth}
                      onChange={(newValue) => {
                        setSelectedMonth(newValue);
                        setRenderIteration(renderIteration + 1);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Stack>
                <Stack direction={"row"} spacing={2} sx={{ ml: "auto" }}>
                  {/*   <CacheStatusBox
                    metaData={metaData}
                    setIsLoading={setIsLoading}
                    snackbarRef={snackbarRef}
                    setRenderIteration={setRenderIteration}
                    renderIteration={renderIteration}
                  /> */}

                  <ApiStatusBox />
                </Stack>
              </Box>
              <Box>
                {errors && (
                  <Stack spacing={1} sx={{ mb: 2, position: "relative" }}>
                    <Typography color="error">
                      Error loading QuickBooks Invoices:
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors)}
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors?.errors)}
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors?.response)}
                    </Typography>
                    <IconButton
                      size="small"
                      variant="outlined"
                      sx={{ position: "absolute", top: 0, right: 0 }}
                      onClick={() => setErrors(null)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "40px",
                  backgroundColor:
                    CONSTANTS.ENV === "PROD" ? "#ebb7b7ff" : "#83ec9eff",
                }}
              >
                {CONSTANTS.ENV === "LOCAL" || CONSTANTS.ENV === "DEV" ? (
                  <Typography sx={{ color: "#000000ff", fontWeight: "bold" }}>
                    You are in {CONSTANTS.ENV} environment
                  </Typography>
                ) : (
                  <Typography sx={{ color: "#000000ff", fontWeight: "bold" }}>
                    You are in PRODUCTION environment - Please exercise caution!
                  </Typography>
                )}
              </Box>

              <EnhancedTable
                type="QuickBooks Invoice (Supports Invoice Id, Account name, and Company name search)"
                headCells={headCells}
                //enableCheckboxes
                api={QBInvoicesApiWrapper()}
                hidePagination
                // actionButtons={actionButtons}
                errors={(e) => {
                  setErrors(e);
                }}
                apiMetaData={(meta) => {
                  setMetaData(meta);
                }}
                defaultSort={"Id"}
                renderIterationWithLoading={renderIteration}
                filterOptions={filterOptions}
                filtersState={[filters, setFilters]}
                handleRowClick={(row) => {
                  handleRowClick(row);
                }}
              />
              <Box sx={{ height: "100px" }}>
                {/* Spacer to prevent content cutoff */}
              </Box>
            </Stack>
          </div>
        </Stack>
      </div>

      <StackedSnackbar ref={snackbarRef} autoHideDuration={3000} />

      {isLoading && <Loader overlay />}

      {single.modals}

      <QBMailerDetailModal
        accountId={selectedRowData}
        open={detailModalOpen}
        selectedMonth={selectedMonth}
        onClose={(e) => {
          setDetailModalOpen(false);
          if (e) {
            setRenderIteration(renderIteration + 1);
          }
        }}
        snackbarRef={snackbarRef}
        metaData={metaData}
      />
    </>
  );
};
